/**
 * The one place lucide-react may be imported (GUIDELINES.md §3.1; enforced by
 * `no-restricted-imports` in eslint.config.js).
 *
 * Icons are exported under their ROLE, not their Lucide name, mirroring
 * skypoint-ui's own `src/icons/index.ts` convention — that module is internal
 * to the library and is deliberately not re-exported from its package root, so
 * this barrel is the app-side equivalent. Naming by role means a component
 * reads `<ConnectionsIcon />` rather than `<Cable />`, and swapping the glyph
 * is a one-line change here.
 *
 * To add an icon: import it from lucide-react below and export it under the
 * role it serves. Never import lucide-react anywhere else.
 */
import type { ComponentType } from "react";
import {
  Activity,
  ArrowRight,
  ArrowUp,
  Bell,
  BellRing,
  Bot,
  Boxes,
  Building2,
  Cable,
  CalendarClock,
  Check,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  CircleAlert,
  CircleCheck,
  CirclePause,
  CircleStop,
  CircleX,
  Clock,
  Copy,
  Database,
  Download,
  Ellipsis,
  ExternalLink,
  Eye,
  FileText,
  Folder,
  FolderOpen,
  HeartPulse,
  Info,
  LayoutDashboard,
  LayoutGrid,
  ListFilter,
  LoaderCircle,
  LogOut,
  Monitor,
  Moon,
  Pause,
  Pencil,
  Play,
  Plug,
  Plus,
  Puzzle,
  RefreshCw,
  RotateCcw,
  ScrollText,
  Search,
  Settings,
  Shield,
  ShieldCheck,
  SlidersHorizontal,
  Sun,
  Table2,
  Trash,
  TriangleAlert,
  Upload,
  Users,
  Workflow,
  X,
} from "lucide-react";

/** The shape every icon satisfies — what SkyNavDrawer and SkyButton accept. */
export type IconComponent = ComponentType<{ className?: string }>;

// Navigation (UX-3). One icon per primary section and per Admin item.
export const OverviewIcon = LayoutDashboard;
export const ConnectionsIcon = Cable;
export const SchedulesIcon = CalendarClock;
export const RunsIcon = Play;
export const TransformationsIcon = Workflow;
export const DestinationIcon = Database;
/** Your data (ADR 0011): where skyData puts the data, in the destination. */
export const YourDataIcon = Database;
/** A table skyData loaded (Overview's imports card). */
export const TableObjectIcon = Table2;
/** skyMonitor: data health. */
export const SkyMonitorIcon = HeartPulse;
export const AdminIcon = Shield;
export const UsersIcon = Users;
export const InstancesIcon = Boxes;
export const ConnectorsIcon = Puzzle;
export const SettingsIcon = Settings;
export const AuditIcon = ScrollText;
export const AppIcon = LayoutGrid;
// Platform administration (FR-20, FR-72): the tenancy tree and the deployment-wide settings.
// Tenants keeps the building the Customers entry showed (round 6 T-04 folded one into the other).
export const TenantsIcon = Building2;
export const PlatformSettingsIcon = SlidersHorizontal;
/** AI apps over MCP (ADR 0009): the admin page, and a person's own connected apps. */
export const AiAppsIcon = Bot;
export const ConnectedAppsIcon = Plug;
/** Release 8B: the page where a person approves a change an AI app asked to make. */
export const ApproveChangeIcon = ShieldCheck;
export const HealthIcon = Activity;

// Status and validation.
export const ValidationSuccessIcon = CircleCheck;
export const ValidationErrorIcon = CircleX;
export const ValidationWarningIcon = TriangleAlert;
export const InfoIcon = CircleAlert;
/** "What does this mean": the info glyph beside a title whose definition is in a tooltip. */
export const DefinitionIcon = Info;
export const PendingIcon = Clock;
export const SpinnerIcon = LoaderCircle;

// Actions.
export const AddIcon = Plus;
export const EditIcon = Pencil;
export const DeleteIcon = Trash;
export const RetryIcon = RefreshCw;
export const RefreshIcon = RefreshCw;
export const ResetIcon = RotateCcw;
export const SearchIcon = Search;
export const DownloadIcon = Download;
export const CheckIcon = Check;
export const DismissIcon = X;
export const SignOutIcon = LogOut;
export const ViewIcon = Eye;
export const PlayIcon = Play;
export const PauseIcon = Pause;
/** "Pause…" a schedule until a day (Session 10, P9); `PauseIcon` already marks "Disable schedule". */
export const PauseUntilIcon = CirclePause;
export const ExternalLinkIcon = ExternalLink;
export const UploadIcon = Upload;
export const BrowseIcon = Folder;
export const FolderOpenIcon = FolderOpen;
export const CancelIcon = CircleStop;
export const CopyIcon = Copy;
export const MoreActionsIcon = Ellipsis;
export const FilterIcon = ListFilter;
export const LogsIcon = FileText;
/** Alert routing: Watch a connection or schedule, and the pressed "Watching" state. */
export const WatchIcon = Bell;
export const WatchingIcon = BellRing;

// Chrome and direction.
export const ChevronDownIcon = ChevronDown;
export const ChevronLeftIcon = ChevronLeft;
export const ChevronRightIcon = ChevronRight;
export const ArrowRightIcon = ArrowRight;
/** Round 4: "N newer runs, back to newest" on a later page of a paged list. */
export const ArrowUpIcon = ArrowUp;

// Appearance (theme toggle).
export const LightModeIcon = Sun;
export const DarkModeIcon = Moon;
export const SystemModeIcon = Monitor;
